import { useState, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types';
import TranscriptAISummaryTab from './TranscriptAISummaryTab'
import AISummaryActionPanel from './AISummaryActionPanel'
import SummaryTab from './SummaryTab'
import { AI_SUMMARY_SUB_TAB, FORMATTED_DATE, FULL_DATE_FORMAT } from '../../../../constants/Constants'
import clsx from 'clsx'
import { formatDate, secondsToTime } from '../../../../shared/utility'

const SummaryDataBox = ({ summary, index, summaryDataLength, localisedValues, startTimeStamp }) => {

    const [activeTab, setActiveTab] = useState(AI_SUMMARY_SUB_TAB.SUMMARY);
    const [searchTranscriptText, setSearchTranscriptText] = useState('')
    const [debouncedSearchTranscriptText, setDebouncedSearchTranscriptText] = useState('')
    const [filteredTranscription, setFilteredTranscription] = useState(summary?.transcription || [])

    const downloadStartTimeStampRef = useRef(startTimeStamp);
    const listStartTimeStampRef = useRef(startTimeStamp);


    const modifiedTranscription = useMemo(() => {
        const transcriptionData = summary?.transcription;

        if (!Array.isArray(transcriptionData) || transcriptionData.length === 0) return [];

        // Initialize ref if not already set
        if (!downloadStartTimeStampRef.current && transcriptionData[0]?.SystemTimeStamp) {
            downloadStartTimeStampRef.current = transcriptionData[0].SystemTimeStamp;
        }

        return transcriptionData.map(item => {
            const elapsedSeconds = item.SystemTimeStamp - downloadStartTimeStampRef.current;
            return { ...item, CallDuration: elapsedSeconds };
        });

    }, [summary?.transcription]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTranscriptText(searchTranscriptText);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTranscriptText]);

    useEffect(() => {
        if (!debouncedSearchTranscriptText.trim()) {
            setFilteredTranscription(summary?.transcription || []);
        } else {
            const lower = debouncedSearchTranscriptText?.trim()?.toLowerCase();
            setFilteredTranscription(
                summary?.transcription?.filter(
                    (item) =>
                        item?.Text?.toLowerCase().includes(lower)
                )
            );
        }
    }, [debouncedSearchTranscriptText, summary?.transcription]);

    return (
        <div className="accordion-item accordion-item-box">
            <div
                className="accordion-header"
                id={`ai-summary-${index + 1}-heading`}
            >
                <button
                    type="button"
                    className={clsx("accordion-button", summaryDataLength === 1 ? "collapse" : "collapsed")}
                    data-bs-toggle="collapse"
                    data-bs-target={`#ai-summary-${index + 1}`}
                    aria-expanded="true"
                    aria-controls={`ai-summary-${index + 1}`}
                >
                    <div className="accordion-header-left">
                        <div className="accordion-header-text">
                            <div className="title">
                                {formatDate(summary.sessionStartTime, FULL_DATE_FORMAT, FORMATTED_DATE)}
                                <span className="accordion-header-arrow">
                                    <span className="for-icon icon-arrow-down"></span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="accordion-header-duration">
                        {localisedValues?.duration}: {secondsToTime(summary.duration || 0)}
                    </div>
                </button>
            </div>
            <div
                id={`ai-summary-${index + 1}`}
                className={clsx("accordion-collapse collapse", summaryDataLength === 1 && "show")}
                aria-labelledby={`ai-summary-${index + 1}-heading`}
            >
                <div className="accordion-body padding-0">
                    <div className="session-summary-box">
                        <div className="summary-tab-nav">
                            <div className="summary-tab-nav-in">
                                <ul className="nav nav-tabs" role="tablist">
                                    <li className="nav-item" role="presentation">
                                        <button
                                            className="nav-link active"
                                            data-bs-toggle="tab"
                                            data-bs-target={`#ai-summary-${index + 1
                                                }-summary`}
                                            type="button"
                                            role="tab"
                                            onClick={() => setActiveTab(AI_SUMMARY_SUB_TAB.SUMMARY)}
                                        >
                                            <span className="nav-item-text">{localisedValues?.summary}</span>
                                        </button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button
                                            className="nav-link"
                                            data-bs-toggle="tab"
                                            data-bs-target={`#ai-summary-${index + 1
                                                }-transcript`}
                                            type="button"
                                            role="tab"
                                            onClick={() => setActiveTab(AI_SUMMARY_SUB_TAB.TRANSCRIPT)}
                                        >
                                            <span className="nav-item-text">{localisedValues?.transcript}</span>
                                        </button>
                                    </li>
                                </ul>
                            </div>
                            <AISummaryActionPanel
                                localisedValues={localisedValues}
                                fileName={summary.fileName}
                                modifiedTranscription={modifiedTranscription}
                                searchTranscriptText={searchTranscriptText}
                                setSearchTranscriptText={setSearchTranscriptText}
                                activeTab={activeTab}
                                aiSummary={summary.aiSummary}
                                summaryFileName={summary.aiSummary?.fileName}
                            />
                        </div>
                        <div className="tab-content summary-tab-content">
                            <div
                                className="tab-pane fade show active"
                                id={`ai-summary-${index + 1}-summary`}
                                role="tabpanel"
                            >
                                <SummaryTab
                                    summary={summary.aiSummary}
                                    localisedValues={localisedValues}
                                />
                            </div>
                            <div
                                className="tab-pane fade show"
                                id={`ai-summary-${index + 1}-transcript`}
                                role="tabpanel"
                            >
                                {Array.isArray(summary?.transcription) && filteredTranscription?.length > 0 ?
                                    <TranscriptAISummaryTab
                                        filteredTranscription={filteredTranscription}
                                        localisedValues={localisedValues}
                                        startTimeStampListRef={listStartTimeStampRef}
                                    /> :
                                    (
                                        <div className="session-summary">
                                            <div className="no-session-summary">
                                                <h3>{localisedValues?.no_result_found}</h3>
                                            </div>
                                        </div>
                                    )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

SummaryDataBox.propTypes = {
    summary: PropTypes.any,
    index: PropTypes.number,
    summaryDataLength: PropTypes.number,
    localisedValues: PropTypes.object,
    startTimeStamp: PropTypes.any,
};

export default SummaryDataBox
